import { useRoute } from '@react-navigation/native';
import React from 'react';

import { TaskFormSkeleton } from '@components/Tasks/TaskFormSkeleton';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { TasksStackScreenProps } from '@navigation/tasks/tasks-stack';
import { localDateFromUTCDate } from '@utils/formatters/date';

import { Task, useGetTaskQuery } from '../../graphql/generated';
import TaskFormStack, { FormValues } from '../Tasks/TaskFormStack';

export const EditTask = ({
  taskId: taskIdProp = '',
}: {
  taskId?: Task['id'];
}) => {
  const route = useRoute<TasksStackScreenProps<'task-edit'>['route']>();

  const { params } = route || {};
  const { taskId = taskIdProp } = params || { taskId: '' };

  const { data: taskData } = useGetTaskQuery({
    variables: {
      id: taskId,
    },
    skip: !taskId,
  });

  // Scoped on "nothing rendered yet" rather than `loading || !taskData`.
  // `loading` also goes true for a background getTask refetch, and
  // unmounting TaskFormStack then would throw away an in-progress edit
  // (EditProject.tsx, TA-10386, has the same rationale).
  const showSkeleton = useMinimumVisible(!taskData);
  if (showSkeleton || !taskData) return <TaskFormSkeleton />;

  const { getTask: task } = taskData || { getTask: {} };

  const {
    startDate: initialStartDate,
    dueDate: initialDueDate,
    dependencies,
    description: initialDescription,
    name: initialName,
  } = task as Task;

  const startDate = initialStartDate && localDateFromUTCDate(initialStartDate);
  const dueDate = initialDueDate && localDateFromUTCDate(initialDueDate);

  const dependencyIds = dependencies?.map((d) => d.id) || [];

  const description = initialDescription || '';
  const name = initialName || '';

  const initialMembers = task.members.map((m) => {
    return {
      ...m.user,
      role: m.role,
    };
  });

  const initialValues: FormValues = {
    ...(!!task && task),
    users: initialMembers,
    ...{ startDate, dueDate, name, description },
    dependencyIds,
  };

  return (
    <TaskFormStack
      taskId={taskId}
      initialValues={initialValues}
      screenName='Edit'
    />
  );
};
